import { type SearchCollectionsQuery, useSearchCollectionsInfiniteQuery } from '@vizit/api';
import { useInfiniteScroll } from '@vizit/hooks';
import * as React from 'react';
import { useCollectionGrid } from '../../providers/collection-grid-provider';
import { transformStateToQueryParams } from '../../providers/collection-grid-provider.state';

interface UseCollectionGridViewerProps {
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
}

/**
 * Hook to fetch portfolio collections for the collection grid viewer with infinite loading
 */
function useCollectionGridViewer(_props: UseCollectionGridViewerProps) {
  const { workspaceId, limit, productCategoryId, imageType, searchTerm, sortOption } = useCollectionGrid();

  const queryVariables = React.useMemo(
    () => ({
      workspace_id: workspaceId ?? '',
      ...transformStateToQueryParams(
        {
          productCategoryId,
          imageType,
          searchTerm,
          sortOption,
        },
        limit,
      ),
    }),
    [workspaceId, limit, productCategoryId, imageType, searchTerm, sortOption],
  );

  const { data, isLoading, error, hasNextPage, hasPreviousPage, isFetchingNextPage, fetchNextPage } =
    useSearchCollectionsInfiniteQuery(queryVariables, {
      enabled: !!workspaceId,
    });

  const collections = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchCollectionsQuery[] } | undefined;
    return infiniteData?.pages?.flatMap((page) => page.data ?? []).filter((item) => item != null) || [];
  }, [data]);

  const totalCount = React.useMemo(() => {
    const infiniteData = data as { pages?: SearchCollectionsQuery[] } | undefined;
    return infiniteData?.pages?.[0]?.total_count ?? 0;
  }, [data]);

  const sentinelRef = useInfiniteScroll({ hasNextPage: hasNextPage ?? false, isFetchingNextPage, fetchNextPage });

  // Use hasPreviousPage to determine loading count
  // hasPreviousPage = false: initial load
  // hasPreviousPage = true: subsequent loads
  const loadingCount = React.useMemo(() => {
    if (isLoading && !hasPreviousPage) {
      return limit;
    }
    if (isFetchingNextPage) {
      return limit;
    }
    return 0;
  }, [isLoading, hasPreviousPage, isFetchingNextPage, limit]);

  return {
    collections,
    totalCount,
    isLoading,
    hasError: !!error,
    hasNextPage,
    isFetchingNextPage,
    sentinelRef,
    loadingCount,
  };
}

export { useCollectionGridViewer };
export type { UseCollectionGridViewerProps };
